import React, { useCallback } from 'react';
import Cookies from 'universal-cookie';
import CryptoJS from 'crypto-js';
import { defaultFile, down_arrow, Excel, group_list_img, pair_list_img, PdfIcon, pptFile, Word } from './images';
import CustomImage from 'components/UI/image/CustomImage';
import moment from 'moment-timezone';

import { MessageReceiverType, PROMPT_TYPE_OPTIONS, ROLE, StoreStatus } from 'static';
import DOMPurify from 'dompurify';
import { userFullNameBuilder } from './userFullNameBuilder';
import { EventAttachment, EventsData } from 'types';

const cookies = new Cookies();
const SECRET_KEY = process.env.REACT_APP_CRYPTO_SECRET_KEY;

const { I_SUPER_ADMIN, P_SUPER_ADMIN, I_LOCAL_ADMIN, P_LOCAL_ADMIN } = ROLE;

export const getAccessToken = () => cookies.get('x-auth-token');

export const expireTime = () => new Date(Date.now() + 8 * (60 * 60 * 1000)); // Token expiry time

// Encrypt Cookie
export const cookieEncryption = (key: string, data: unknown) => {
  if (key && data) {
    let ciphertext = CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();

    // cookies.set(key, ciphertext, { domain: process.env.REACT_COOKIE_DOMAIN, path: '/', expires: expireTime(), });
    cookies.set(key, ciphertext, { path: '/', expires: expireTime() });
  }
};

export const encryption = (data: object) => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
};

export const decryption = (data: string) => {
  // A malformed value or a key mismatch (e.g. a payload encrypted server-side
  // with a different secret than this build's) makes JSON.parse throw on
  // garbage bytes instead of just failing to decrypt. Degrade to null rather
  // than let that propagate uncaught, since this runs at module scope (below)
  // and inside render paths where an uncaught throw takes down the whole app.
  try {
    let bytes = CryptoJS.AES.decrypt(decodeURIComponent(data), SECRET_KEY);
    const cryptoBytes = bytes.toString(CryptoJS.enc.Utf8);
    return cryptoBytes ? JSON.parse(cryptoBytes) : null;
  } catch (e) {
    return null;
  }
};

// Decrypt Cookie
export const cookieDecryption = (key: string) => {
  const data = cookies.get(key);
  let decryptedData = null;

  if (key && data) {
    try {
      let bytes = CryptoJS.AES.decrypt(decodeURIComponent(data), SECRET_KEY);
      decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (e) {
      decryptedData = null;
    }
  }

  return decryptedData;
};

export const user = cookieDecryption('user');
export const masterUser = cookieDecryption('masterUser');

export function formattedDate(value: string): string {
  // returns mm/dd/yyyy
  if (value) {
    const date = new Date(value);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }
  return '';
}

export function getTimeFormat(time: any, timezone: any) {
  const userTZ = timezone?.utc || 'UTC';
  const now = moment().tz(userTZ);
  const msgTime = moment(time).tz(userTZ);

  const startOfToday = now.clone().startOf('day');

  if (msgTime.isSameOrAfter(startOfToday)) {
    return msgTime.format('h:mm A');
  } else if (now.year() === msgTime.year()) {
    return msgTime.format('MMM D');
  } else {
    return msgTime.format('MM/DD/YYYY');
  }
}

export const checkTimeZoneDateEvents = (date: any, timezone: any) => {
  let dateTime = new Date(date);

  let convertedTimeforListing = dateTime.toLocaleDateString('en-US', {
    timeZone: timezone?.utc,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
  });
  let convertedTimeforForm = dateTime.toLocaleTimeString('en-US', {
    timeZone: timezone?.utc,
    hour: 'numeric',
    minute: 'numeric',
  });
  let convertedDateforListing = dateTime.toLocaleDateString('en-US', {
    timeZone: timezone?.utc,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  return { convertedTimeforListing, convertedTimeforForm, convertedDateforListing };
};
function buildFormData(formData: FormData, data: any, parentKey?: string) {
  if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
    Object.keys(data).forEach(key => {
      buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
    });
  } else {
    const value = data == null ? '' : data;
    formData.append(parentKey!, value);
  }
}

export function jsonToFormData(data: any): FormData {
  const formData = new FormData();
  buildFormData(formData, data);
  return formData;
}

export function generateColor() {
  const red = Math.floor(Math.random() * 100);
  const green = Math.floor(Math.random() * 100);
  const blue = Math.floor(Math.random() * 100);

  return '#' + componentToHex(red) + componentToHex(green) + componentToHex(blue);
}

export function sanitizeProfilePicSource(source: string) {
  return DOMPurify.sanitize(source, {
    ALLOWED_URI_REGEXP: /^https?:\/\//,
  });
}

export function getEventApprovalLine(row: EventsData): string | null {
  if (row.approval === 'Approved' && row.approvedBy) {
    return `Approved by ${row.approvedBy}`;
  }
  if (row.approval === 'Declined' && row.rejectedBy) {
    return `Rejected by ${row.rejectedBy}`;
  }
  return null;
}

function componentToHex(c: number) {
  const hex = c.toString(16);
  return hex.length === 1 ? '0' + hex : hex;
}

export const getCustomAvatar = (text: any, classText?: any, isFontSize?: any) => {
  return isFontSize ? (
    <span
      className={`user_pic ${classText}`}
      style={{ background: stringToHslColor(text), color: 'white', fontSize: '11px' }}>
      {generateProfileNameImage(text)}
    </span>
  ) : (
    <span className={`user_pic ${classText}`} style={{ background: stringToHslColor(text), color: 'white' }}>
      {generateProfileNameImage(text)}
    </span>
  );
};

export const stringToHslColor = (str: any, s = 30, l = 80, darkness = 50) => {
  let hash = 0;
  for (let i = 0; i < str?.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let h = hash % 360;

  // Adjust lightness for darkness
  l = Math.max(0, l - darkness);

  return 'hsl(' + h + ', ' + s + '%, ' + l + '%)';
};

export function sanitizeKey(inputString: any) {
  const specialChars = {
    '(': 40,
    ')': 41,
    '/': 47,
    '?': 63,
    '[': 91,
    ']': 93,
    "'": 39,
    ',': 44,
    '.': 46,
  };

  let sanitizedString = '';
  for (let char of inputString) {
    // @ts-ignore
    if (specialChars[char] !== undefined) {
      // @ts-ignore
      sanitizedString += `_${specialChars[char]}`;
    } else {
      sanitizedString += char;
    }
  }

  return sanitizedString;
}

export function retrieveOriginalValue(sanitizedString: any) {
  const specialChars = {
    40: '(',
    41: ')',
    47: '/',
    63: '?',
    91: '[',
    93: ']',
    39: "'",
    44: ',',
    46: '.',
  };

  let originalValue = '';
  let i = 0;
  while (i < sanitizedString.length) {
    if (sanitizedString[i] === '_' && i + 3 < sanitizedString.length) {
      const asciiValue = parseInt(sanitizedString.substring(i + 1, i + 4), 10);
      // @ts-ignore
      if (!isNaN(asciiValue) && specialChars[asciiValue] !== undefined) {
        // @ts-ignore
        originalValue += specialChars[asciiValue];
        i += 4;
        continue;
      }
    }
    originalValue += sanitizedString[i];
    i++;
  }

  return originalValue;
}

export function scrollToTopSmooth() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
}

export const areAllFieldsNotBlank = (fieldValues: any): boolean => {
  for (const value of Object.values(fieldValues)) {
    // Check if the value is falsy or an empty string
    if (value === '') {
      return false;
    }
  }
  return true;
};

export const setLabel = (user: any) => {
  switch (true) {
    case !!user?.role:
      return userFullNameBuilder.build(user?.preferredFname, user?.fname, user?.lname);
    case !!user?.groupName:
      return user?.groupName;

    default:
      return `${user?.mentorId?.preferredFname ?? ''} ${user?.mentorId?.lname ?? ''} Vs ${user?.menteeId?.preferredFname ?? ''} ${user?.menteeId?.lname ?? ''}`;
  }
};

export const getElement = (user: any) => {
  switch (true) {
    case !!user?.role: {
      return user?.profilePic && user?.profilePic !== '' ? (
        <CustomImage src={user?.profilePic} style={{ borderRadius: '100%' }} />
      ) : null;
    }
    case !!user?.groupName:
      return <img src={group_list_img} alt="" />;

    default:
      return (
        <div className="pairs_match pairs_img_view">
          <div className="user_pic">
            <span className="common_match">
              {!!user?.menteeId?.profilePic ? (
                <img
                  className="generate_pic top_pic"
                  src={sanitizeProfilePicSource(user?.menteeId?.profilePic) || undefined}
                  alt=""
                />
              ) : (
                <img
                  src={generateCustomAvatar(
                    user?.menteeId?.preferredFname + ' ' + user?.menteeId?.lname,
                    'small-caps bold 75px Montserrat_regular'
                  )}
                  alt="profile-pic"
                  className="generate_pic top_pic"
                  style={{ borderRadius: '50%', objectFit: 'cover' }}
                />
              )}

              {!!user?.mentorId?.profilePic ? (
                <img
                  className="generate_pic bottom_pic"
                  src={sanitizeProfilePicSource(user?.menteeId?.profilePic) || undefined}
                  alt=""
                />
              ) : (
                <img
                  src={generateCustomAvatar(
                    user?.mentorId?.preferredFname + ' ' + user?.mentorId?.lname,
                    'small-caps bold 75px Montserrat_regular'
                  )}
                  alt="profile-pic"
                  className="generate_pic bottom_pic"
                  style={{ borderRadius: '50%', objectFit: 'cover' }}
                />
              )}
            </span>
          </div>
        </div>
      );
  }
};

export const getImage = (user: any) => {
  switch (true) {
    case !!user?.role:
      return user?.profilePic && user?.profilePic !== '' ? user?.profilePic : null;
    case !!user?.groupName:
      return group_list_img;

    default:
      return pair_list_img;
  }
};

/**
 * Maps a status or admin-role label to its badge class.
 *
 * NOTE: components/searchable-dropdown/DropdownWithSearchInput.tsx keeps its own status-only copy
 * of this switch, because it must not apply role classes to its role-valued callers. Adding a
 * status here alone is not enough: add it there too, or the filter dropdown renders it uncoloured.
 */
export function getClassName(params: string): string {
  switch (params) {
    case I_SUPER_ADMIN:
      return 'super_admin';
    case I_LOCAL_ADMIN:
      return 'im_local_admin';
    case P_SUPER_ADMIN:
      return 'partner_super_admin';
    case P_LOCAL_ADMIN:
      return 'partner_local_admin';
    case 'Invited':
      return 'invited';
    case 'In Progress':
      return 'inprogress';
    case 'Completed - Pending Pre-Match To Dos':
      return 'completed';
    case 'Pre-Match Pending':
      return 'completed';
    case 'Completed':
      return 'completed';
    case 'Not Started':
      return 'notstarted';
    case 'Needs Approval':
      return 'needs_approval';
    case 'Ready to Be Matched':
      return 'ready_to_be_matched';
    case 'Needs Approval':
      return 'needs_approval';
    case 'Matched':
      return 'matched';
    case 'Matching In progress':
      return 'matching_inprogress';
    case 'Draft':
      return 'invited';
    case 'Disabled':
      return 'invited';
    case 'Matched Not Registered':
      return 'matched';
    default:
      return '';
  }
}

export function Decryption(data: any) {
  try {
    let bytes = CryptoJS.AES.decrypt(data, process.env.REACT_APP_CRYPTO_SECRET_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (e) {}
}

export const Encryption = (data: any) => {
  try {
    return CryptoJS.AES.encrypt(data, process.env.REACT_APP_CRYPTO_SECRET_KEY).toString();
  } catch (e) {}
};

function isValidURL(url: any) {
  // Regular expression pattern to match a URL
  var urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/;

  // Check if the given URL Matches the pattern
  return urlPattern.test(url);
}

export function getFileTypeFromUrl(url: any) {
  if (!isValidURL(url)) {
    return;
  }

  const parsedUrl = new URL(url);

  const pathname = parsedUrl.pathname;
  const extension = pathname.substring(pathname.lastIndexOf('.')).toLowerCase();

  const mimeType = extensionToMimeType(extension);
  return mimeType;
}

export type FileInfo = { type: 'audio' } | { type: 'file'; icon: string; name: string };

export const getFileInfo = (fileUrl: string): FileInfo | null => {
  if (!fileUrl) return null;

  const mimeType = getFileTypeFromUrl(fileUrl);
  const name = getFilenameFromUrlPath(fileUrl) ?? 'file';

  if (!mimeType) return { type: 'file', icon: defaultFile, name };

  switch (mimeType) {
    case 'application/pdf':
      return { type: 'file', icon: PdfIcon, name };
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return { type: 'file', icon: Word, name };
    case 'application/vnd.ms-powerpoint':
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return { type: 'file', icon: pptFile, name };
    case 'application/vnd.ms-excel':
    case 'text/csv':
      return { type: 'file', icon: Excel, name };
    case 'audio/mpeg':
    case 'application/octet-stream':
      return { type: 'audio' };
    default:
      return { type: 'file', icon: defaultFile, name };
  }
};

export const getEventAttachmentDisplayName = (attachment: EventAttachment): string =>
  attachment.filename || getFilenameFromUrlPath(attachment.url) || 'Attachment';

/**
 * Splits an event's attachments into the large hero image (first-uploaded image, by
 * upload order) and the remaining attachments as alphabetically sorted file rows.
 */
export const getEventAttachmentSections = (
  attachments: EventAttachment[] | undefined | null
): { heroImage: EventAttachment | null; fileRows: EventAttachment[] } => {
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return { heroImage: null, fileRows: [] };
  }

  const validAttachments = attachments.filter((attachment): attachment is EventAttachment => Boolean(attachment));

  // Attachments saved without metadata have a null type; fall back to the URL's extension.
  const imageAttachments = validAttachments.filter(attachment =>
    (attachment.type ?? getFileTypeFromUrl(attachment.url))?.startsWith('image/')
  );
  const heroImage = imageAttachments.length
    ? imageAttachments.reduce((first, current) => (current.order < first.order ? current : first))
    : null;

  const fileRows = validAttachments
    .filter(attachment => attachment !== heroImage)
    .sort((a, b) =>
      getEventAttachmentDisplayName(a).localeCompare(getEventAttachmentDisplayName(b), undefined, {
        sensitivity: 'base',
      })
    );

  return { heroImage, fileRows };
};

function extractFileName(inputString: any) {
  const parts = inputString.split('-');
  if (parts.length > 0) {
    const fileNameWithExtension = parts[parts.length - 1];
    const fileName = fileNameWithExtension.substring(0, fileNameWithExtension.lastIndexOf('.'));
    return fileName.replace(/-/g, ' ');
  } else {
    return null; // No valid file name found
  }
}

// export function getFilenameFromPath(filePath: any) {
//   if (!filePath) {
//     return;
//   }

//   const parts = filePath?.split("/");
//   const lastPart = parts[parts?.length - 1];
//   return extractFileName(lastPart);
// }
export function getFilenameFromPath(filePath: string | null | undefined): string | undefined {
  if (!filePath) {
    return;
  }

  // If there are hyphens, grab the string after the last hyphen and replace underscores
  if (filePath.includes('-')) {
    const parts = filePath.split('-');
    const lastPart = parts[parts.length - 1];
    return lastPart.replace(/_/g, ' ').replace(/\.pdf$/, '');
  }

  // If no hyphens, and the filename has spaces followed by a sequence of numbers and .pdf
  const match = /(.*\S) \d+\.pdf$/.exec(filePath);
  if (match && match[1]) {
    return match[1];
  }

  // If none of the above conditions are met, return undefined
  return;
}

// export function getFilenameFromUrlPathWithoutExtension(filePath?: string | null): string | undefined {
//   if (!filePath) return;
//
//   const filename = filePath.split('?')[0].split('#')[0].split('/').pop();
//
//   if (!filename) return;
//
//   return decodeURIComponent(filename)
//     .replace(/\.[^/.]+$/, '')
//     .replace(/_/g, ' ')
//     .trim();
// }

export function getFilenameFromUrlPath(filePath?: string | null): string | undefined {
  if (!filePath) return;

  let filename = filePath.split('?')[0].split('#')[0].split('/').pop();
  if (!filename) return;

  // Remove backend timestamp before extension (like _1770899601946)
  filename = filename.replace(/_\d+(?=\.\w+$)/, '');

  // decodeURIComponent never converts '+' to a space (only %XX sequences),
  // so normalize '+'-encoded spaces before decoding.
  return decodeURIComponent(filename.replace(/\+/g, ' ')).trim();
}

export function truncateFilename(filename?: string | null, maxLength = 30): string {
  if (!filename) return '';

  const lastDot = filename.lastIndexOf('.');
  const hasExt = lastDot > 0 && lastDot < filename.length - 1;
  const base = hasExt ? filename.slice(0, lastDot) : filename;
  const ext = hasExt ? filename.slice(lastDot) : '';

  if (base.length <= maxLength) return filename;

  return `${base.slice(0, maxLength)}…${ext}`;
}

function extensionToMimeType(extension: any) {
  // Define mappings for common extensions to MIME types
  const extensionToMime = {
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.mp3': 'audio/mpeg',
    '.pdf': 'application/pdf',
    '.doc': 'application/msword', // Microsoft Word document
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // Microsoft Word document (OpenXML)
    '.ppt': 'application/vnd.ms-powerpoint', // Microsoft PowerPoint presentation
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation', // Microsoft PowerPoint presentation (OpenXML)
    '.xls': 'application/vnd.ms-excel', // Microsoft Excel spreadsheet
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // Microsoft Excel spreadsheet (OpenXML)
    '.csv': 'text/csv', // Comma-separated values
    // Add more extensions and MIME types as needed
  };

  // @ts-ignore
  return extensionToMime[extension] || 'application/octet-stream'; // Default to binary if not found
}

export const mimeMap: Record<string, string> = {
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/pdf': 'pdf',
  'audio/mpeg': 'mp3',
  'text/csv': 'csv',
  'image/jpeg': 'jpg',
  'image/png': 'png',
};

export const mimeTypeToExtension = (mimeType: string) => {
  return mimeMap[mimeType] || '';
};

export function formatSeconds(seconds: any) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  const parts = [];

  if (hours > 0) {
    parts.push(`${hours.toString().padStart(2, '0')}`);
  }

  parts.push(`${minutes.toString().padStart(2, '0')}`);
  parts.push(`${remainingSeconds.toString().padStart(2, '0')}`);

  return parts.join(':');
}

export const audioErrorHandler = (error: any) => {
  switch (error.name) {
    case 'AbortError': //error from navigator.mediaDevices.getUserMedia
      console.log('An AbortError has occured.');
      break;
    case 'NotAllowedError': //error from navigator.mediaDevices.getUserMedia
      console.log('A NotAllowedError has occured. User might have denied permission.');
      break;
    case 'NotFoundError': //error from navigator.mediaDevices.getUserMedia
      console.log('A NotFoundError has occured.');
      break;
    case 'NotReadableError': //error from navigator.mediaDevices.getUserMedia
      console.log('A NotReadableError has occured.');
      break;
    case 'SecurityError': //error from navigator.mediaDevices.getUserMedia or from the MediaRecorder.start
      console.log('A SecurityError has occured.');
      break;
    case 'TypeError': //error from navigator.mediaDevices.getUserMedia
      console.log('A TypeError has occured.');
      break;
    case 'InvalidStateError': //error from the MediaRecorder.start
      console.log('An InvalidStateError has occured.');
      break;
    case 'UnknownError': //error from the MediaRecorder.start
      console.log('An UnknownError has occured.');
      break;
    default:
      console.log('An error occured with the error name ' + error.name);
  }
};

export async function getAudioDuration(mp3Blob: any): Promise<string> {
  // @ts-ignore
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();

  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = async () => {
      const arrayBuffer = fileReader.result;

      try {
        // @ts-ignore
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        // Calculate the duration based on the AudioBuffer's duration property
        const audioDuration = Math.floor(audioBuffer.duration).toFixed(0);
        resolve(audioDuration);
      } catch (error) {
        reject(error);
      }
    };

    fileReader.readAsArrayBuffer(mp3Blob);
  });
}

export async function blobToMP3(blob: any) {
  const mp3Blob = new Blob([blob], { type: 'audio/mp3' });
  // Create a new File object from the Blob
  const mp3File = new File([mp3Blob], 'audio.mp3', {
    type: 'audio/mp3',
  });
  const duration = await getAudioDuration(mp3Blob);

  return { file: mp3File, duration };
}

export async function blobToM4A(blob: any) {
  const mp3Blob = new Blob([blob], { type: 'audio/mp4' });
  const mp3File = new File([mp3Blob], 'audio.m4a', {
    type: 'audio/mp4',
  });

  const duration = await getAudioDuration(mp3Blob);

  return { file: mp3File, duration };
}

export async function blobToWebm(blob: Blob) {
  const file = new File([blob], 'audio.webm', { type: blob.type });
  const duration = await getAudioDuration(blob);

  return { file, duration };
}

export const generateProfileNameImage = (name: any) => {
  const [fname, lname] = name.split(/\s+/);
  return `${fname?.toUpperCase()?.[0]}${lname?.toUpperCase()?.[0]}`;
};

export const isDataBlankHandler = (data: any) => {
  return data ? data : '--';
};

export const isBooleanDataBlankHandler = (data: any) => {
  return data ? (data ? 'Yes' : 'No') : '--';
};

export const getIsFieldIsBlank = (formValues: any) => {
  const fieldValue = {
    preferredfname: formValues.preferredfname,
    lname: formValues.lname,
    email: formValues.email,
  };

  let allFiled = areAllFieldsNotBlank(fieldValue);
  return allFiled;
};

export const createSortSpan = (
  fieldName: string,
  headerType: any,
  sortType: any,
  handleSort: (nextHeaderType: string) => void
) => (
  <span
    className={`down_arrow ${headerType === fieldName && 'short_list'} ${headerType === fieldName && (sortType === 'des' || sortType === 'desc') && 'descending'} cursor_pointer ml_10`}
    onClick={() => handleSort(fieldName)}>
    <img src={down_arrow} alt="down_arrow" />
    {headerType === fieldName && (sortType === 'des' || sortType === 'desc') ? (
      <span className="tooltip tooltip_descending">Sort Descending</span>
    ) : (
      <span className="tooltip tooltip_ascending">Sort Ascending</span>
    )}
  </span>
);

// Creating virtual optimistic chat message for immediate rendering
export const createPendingMessage = ({
  type,
  content = '',
  fileUrl = null,
  file = null,
  fileType = null,
  audioDuration = null,
  replyBackMessage = null,
  contentToSend = null,
}: {
  type: 'Text' | 'File' | 'Media' | 'Quote' | 'Content';
  content?: string;
  fileUrl?: string | null;
  file?: any;
  fileType?: any;
  audioDuration?: number | null;
  replyBackMessage?: any | null;
  contentToSend?: any | null;
}) => {
  const user = cookieDecryption('user');
  const clientMessageId = crypto.randomUUID();
  const quote = replyBackMessage
    ? {
        quoteMessageId: replyBackMessage._id,
        sender: replyBackMessage.sender,
        type: replyBackMessage.type,
        content: replyBackMessage.content,
        createdAt: replyBackMessage.createdAt,
      }
    : null;

  const pendingMessage: any = {
    _id: clientMessageId,
    clientMessageId,
    createdAt: new Date().toISOString(),
    status: StoreStatus.PENDING,
    category: 'Private Prompt',
    receiverType: MessageReceiverType.GROUP,
    type,
    content: type === 'Text' || type === 'Quote' || type === 'Content' ? content : '',
    ...(type === 'Content' && contentToSend?._id ? { contentId: contentToSend?._id } : {}),
    sender: {
      userId: user.userId,
      preferredFname: user.preferredFname,
    },
    reactions: [],
    quote,
  };

  if (type === 'File' || type === 'Media') {
    pendingMessage.fileUrl = fileUrl;
    pendingMessage.file = file;
    pendingMessage.fileType = fileType;
  }

  if (type === 'File' && audioDuration) {
    pendingMessage.audioFileDuration = audioDuration;
  }

  if (type === 'Content' && contentToSend?._id) {
    pendingMessage.contentDetails = {
      _id: contentToSend?._id,
      fileName: contentToSend?.fileName,
      category: contentToSend?.category,
      type: contentToSend?.type,
      contentLink: contentToSend?.contentLink || '',
      thumbnailFile: contentToSend?.thumbnailFile || '',
    };
  }

  return pendingMessage;
};

export const getFirstString = (str: any) => {
  const words = str.split(' ');
  const firstLetter = words[0].charAt(0);
  const secondLetter = words[1].charAt(0);
  return `${firstLetter}${secondLetter}`;
};

export const generateCustomAvatar = (text: string, style: string) => {
  const canvas = document.createElement('canvas');
  const context: any = canvas.getContext('2d');

  // Draw background
  context.fillStyle = stringToHslColor(text);
  context.fillRect(0, 0, canvas.width, canvas.height);

  // Draw text
  // context.font = " bold 75px Assistant Montserrat_regular";
  context.font = style;
  context.fillStyle = '#fff';
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  let first = text?.split(' ')[0]?.charAt(0)?.toUpperCase();
  let last = text?.split(' ')[1]?.charAt(0)?.toUpperCase();

  if (!last) {
    last = text?.split(' ')[0]?.charAt(1)?.toUpperCase() || text?.split(' ')[0]?.charAt(0)?.toUpperCase();
  }
  if (!first) {
    first = 'S';
    last = 'U';
  }

  context.fillText(first + last, canvas.width / 2, canvas.height / 2);

  return canvas.toDataURL('image/png');
};

export const getDurationDate = (duration: any, date: string) => {
  let seconds = duration.asSeconds();
  let minutes = duration.asMinutes();
  let hours = duration.asHours();
  let days = duration.asDays();
  let months = duration.asMonths();
  let years = duration.asYears();

  if (minutes >= 525600) {
    return `${years.toFixed()} year ago`;
  } else if (months >= 1) {
    return moment(date).format('MMM DD');
  } else if (days < 2) {
    if (seconds < 60) {
      return 'now';
    } else if (minutes <= 1) {
      return `${minutes?.toString()?.split('.')[0]} min ago`;
    } else if (minutes > 1 && minutes < 60) {
      return `${minutes?.toString()?.split('.')[0]} mins ago`;
    } else if (minutes >= 60 && minutes < 120) {
      return `${hours?.toString()?.split('.')[0]} hour ago`;
    } else if (minutes >= 120 && minutes < 1440) {
      return `${hours?.toString()?.split('.')[0]} hours ago`;
    } else {
      return `${days?.toString()?.split('.')[0]} day ago`;
    }
  } else if (days > 1) {
    return `${days?.toString()?.split('.')[0]} days ago`;
  }
};

export const autocompleteOff = () => {
  const inputElements: any = document.getElementsByTagName('input');
  if (inputElements) {
    for (const inputElement of inputElements) {
      inputElement.setAttribute('autocomplete', 'off');
    }
  }
};

export const firstLastNameRegex = /^[a-zA-Z0-9\s!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~\u00C0-\u017F]+$/;

export function trimObjectValues(obj: any): any {
  const trimmedObject: any = {};

  for (const key in obj) {
    if (typeof obj[key] === 'string') {
      trimmedObject[key] = obj[key].trim();
    } else {
      trimmedObject[key] = obj[key];
    }
  }

  return trimmedObject;
}

export function formatMessageDate(inputDate: any, timezone: any) {
  if (!inputDate || !moment(inputDate).isValid()) {
    return 'Invalid date';
  }

  const userTimezone = timezone?.utc || 'UTC';
  const date = moment(inputDate).tz(userTimezone);
  const now = moment().tz(userTimezone);
  const today = now.clone().startOf('day');
  const oneYearAgo = now.clone().subtract(1, 'year');

  if (date.isSame(today, 'day')) {
    return `Today at ${date.format('h:mm A')}`;
  } else if (date.isBefore(oneYearAgo)) {
    return `${date.format('M/D/YY')} at ${date.format('h:mm A')}`;
  } else {
    return `${date.format('MMM D')} at ${date.format('h:mm A')}`;
  }
}

export const UTCToPartnerTimeZone = (date: any, timezone: any) => {
  moment.utc(date).tz(timezone?.utc);
  return moment.utc(date).tz(timezone?.utc);
};

export const hasEventStarted = (startDate: any, timezone: any) => {
  return UTCToPartnerTimeZone(startDate, timezone).isSameOrBefore(moment());
};

export const UTCToFormattedPartnerTimeZone = (date: any, timezone: any) => {
  return moment.utc(date).tz(timezone?.utc)?.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
};

// Full timestamp, e.g. May 11, 2026 1:26 PM. Falls back to local when no
// timezone is given, mirroring formatStandardDateWithTimezone, so a cell and
// its tooltip never name two different clock times.
export const formatFileImportedAt = (date: any, timezone?: any) => {
  if (!date) return '--';

  const givenDate = timezone?.utc ? moment.utc(date).tz(timezone.utc) : moment.utc(date).local();

  return givenDate.isValid() ? givenDate.format('MMMM D, YYYY h:mm A') : '--';
};

export function formatSentPromptsDate(dateString: string) {
  return formatStandardDate(dateString);
}

// Safe date formatter that handles timezone conversion properly
export function formatStandardDateWithTimezone(date: any, timezone: any) {
  if (!date) return '--';

  // Convert UTC date to partner timezone if timezone is provided
  let givenDate, currentDate;
  if (timezone?.utc) {
    givenDate = moment.utc(date).tz(timezone.utc);
    currentDate = moment().tz(timezone.utc);
  } else {
    givenDate = moment(date);
    currentDate = moment();
  }

  if (!givenDate.isValid()) {
    return '--';
  }

  if (givenDate.isSame(currentDate, 'day')) {
    return givenDate.format('h:mm A'); // e.g. 2:10 PM
  }

  if (givenDate.year() === currentDate.year()) {
    return givenDate.format('MMM D'); // e.g. Feb 9
  }

  return givenDate.format('M/D/YY'); // e.g. 12/27/24
}

export function formatPairDate(dateString: string) {
  return formatStandardDate(dateString);
}

export function formatStandardDate(dateString: string) {
  if (!dateString || dateString === 'Invalid date') {
    return '--';
  }

  // Convert UTC date to local timezone
  const givenDate = moment.utc(dateString).local();
  const currentDate = moment();

  if (!givenDate.isValid()) {
    return '--';
  }

  if (givenDate.isSame(currentDate, 'day')) {
    return givenDate.format('h:mm A'); // e.g. 2:10 PM
  }

  if (givenDate.year() === currentDate.year()) {
    return givenDate.format('MMM D'); // e.g. Feb 9
  }

  return givenDate.format('M/D/YY'); // e.g. 12/27/24
}

export function formatEventDateCompact(startDateString: string, endDateString?: string) {
  // For Events page when space is limited (ie, in tables or cards): Fri, May 16, 2025 at 3:00 PM
  // Displays in user's current local timezone
  if (!startDateString || startDateString === 'Invalid date') {
    return '--';
  }

  // Convert UTC to user's local timezone
  const startDate = moment.utc(startDateString).local();
  if (!startDate.isValid()) {
    return '--';
  }

  return startDate.format('ddd, MMM D, YYYY [at] h:mm A'); // e.g. Fri, May 16, 2025 at 3:00 PM
}

export function formatEventDateExtended(
  startDateString: string,
  endDateString: string,
  timezone?: string,
  timezoneUtc?: string,
  includeTimezone: boolean = false
) {
  // For Events page when more space is available (ie, on the Event Details page): Friday, May 16, 2025 at 3:00 PM - 4:15 PM EDT
  // Converts to the specified timezone (if provided) or displays in local timezone
  // includeTimezone: flag to control whether to append timezone abbreviation to the result
  if (!startDateString || startDateString === 'Invalid date') {
    return '--';
  }

  let startDate, endDate;

  if (timezoneUtc) {
    // Convert UTC to the specified timezone
    startDate = moment.utc(startDateString).tz(timezoneUtc);
    endDate = endDateString ? moment.utc(endDateString).tz(timezoneUtc) : null;
  } else {
    // Convert UTC to local timezone
    startDate = moment.utc(startDateString).local();
    endDate = endDateString ? moment.utc(endDateString).local() : null;
  }

  if (!startDate.isValid()) {
    return '--';
  }

  let result = startDate.format('dddd, MMM D, YYYY [at] h:mm A');

  if (endDate && endDate.isValid()) {
    result += ` - ${endDate.format('h:mm A')}`;
  }

  if (includeTimezone && timezone) {
    result += ` ${timezone}`;
  }

  return result; // e.g. Friday, May 16, 2025 at 3:00 PM - 4:15 PM EDT (when includeTimezone is true)
}

export const checkTimeZoneDate = (date: any, timezone: any) => {
  // Check if the timezone object and its 'utc' property are defined
  if (timezone && timezone.utc) {
    // Use the timezone string from the timezone object
    const convertedTime = moment.utc(date).toDate().toLocaleDateString('en-US', {
      timeZone: timezone.utc,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return convertedTime;
  } else {
    // Handle the case when the timezone or its 'utc' property is undefined
    console.error('Invalid or missing timezone provided');
    return null; // Or any other suitable fallback value
  }
};
export function PartnerTimeZoneToUTC(date: any, timezone: any) {
  var m = moment.tz(date, 'MM/DD/YYYY h:mm A', timezone?.utc).utc().format('');
  return m;
}
export const raceTag = [
  {
    label: 'Asian / Pacific Islander',
    value: 'Asian / Pacific Islander',
  },
  {
    label: 'Black / African American',
    value: 'Black / African American',
  },
  {
    label: 'Hispanic or Latinx (May be of any race)',
    value: 'Hispanic or Latinx (May be of any race)',
  },
  {
    label: 'I identify with a race not listed here',
    value: 'I identify with a race not listed here',
  },
  {
    label: 'I would prefer not to indicate my race',
    value: 'I would prefer not to indicate my race',
  },
  {
    label: 'Native American (Aleutian Islander)',
    value: 'Native American (Aleutian Islander)',
  },
  {
    label: 'White (Non-Hispanic)',
    value: 'White (Non-Hispanic)',
  },
];

export function isSixDaysAgo(dateString: string) {
  const date = moment(dateString);
  const currentDate = moment();
  const differenceInDays = Math.abs(currentDate.diff(date, 'days'));
  return differenceInDays <= 6;
}

export function getClassNamePairMentor(obj: any) {
  let className = '';
  switch (true) {
    case !Object?.keys(obj)?.length:
      className = 'gray_chat_box';
      break;
    case obj?.isEventAttendByMentor &&
      obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentor &&
      obj?.isEventAcceptByMentor:
      className = 'pr_event_msg';
      break;
    case !obj?.isEventAttendByMentor &&
      obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentor &&
      obj?.isEventAcceptByMentor:
      className = 'pr_event_invitation';
      break;
    case !obj?.isEventAttendByMentor &&
      obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentor &&
      obj?.isEventAcceptByMentor:
      className = 'pr_event_invitation_decline';
      break;
    case obj?.isEventAttendByMentor &&
      obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentor &&
      !obj?.isEventAcceptByMentor:
      className = 'pr_event_msg';
      break;
    case obj?.isEventAttendByMentor &&
      obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentor &&
      obj?.isEventAcceptByMentor:
      className = 'pr_event_msg_pair';
      break;
    case obj?.isEventAttendByMentor &&
      !obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentor &&
      obj?.isEventAcceptByMentor:
      className = 'pr_border_event_msg';
      break;
    case !obj?.isEventAttendByMentor &&
      obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentor &&
      !obj?.isEventAcceptByMentor:
      className = 'pr_border_onl_box';
      break;
    case !obj?.isEventAttendByMentor &&
      !obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentor &&
      !obj?.isEventAcceptByMentor:
      className = 'both_msg_box';
      break;
    case !obj?.isEventAttendByMentor &&
      !obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentor &&
      obj?.isEventAcceptByMentor:
      className = 'pr_event_invitation_accept';
      break;
    case obj?.isEventAttendByMentor &&
      !obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentor &&
      !obj?.isEventAcceptByMentor:
      className = 'pr_event_msg_ind';
      break;
    case obj?.isEventAttendByMentor &&
      !obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentor &&
      obj?.isEventAcceptByMentor:
      className = 'pr_event_msg_ind';
      break;
    case !obj?.isEventAttendByMentor &&
      obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentor &&
      !obj?.isEventAcceptByMentor:
      className = 'pr_msg_box';
      break;
    case obj?.isEventAcceptByMentor &&
      !obj?.isEventAttendByMentor &&
      !obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentor:
      className = 'pr_border_not_event_msg';
      break;
    case obj?.isEventAttendByMentor &&
      obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentor &&
      !obj?.isEventAcceptByMentor:
      className = 'pr_event_msg_pair';
      break;
    default:
      className = '';
  }
  return className;
}

export function getClassNamePairMentee(obj: any) {
  let className = '';
  switch (true) {
    case !Object?.keys(obj)?.length:
      className = 'gray_chat_box';
      break;
    case obj?.isEventAttendByMentee &&
      obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentee &&
      obj?.isEventAcceptByMentee:
      className = 'pr_event_msg';
      break;
    case !obj?.isEventAttendByMentee &&
      obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentee &&
      obj?.isEventAcceptByMentee:
      className = 'pr_event_invitation';
      break;
    case !obj?.isEventAttendByMentee &&
      obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentee &&
      obj?.isEventAcceptByMentee:
      className = 'pr_event_invitation_decline';
      break;
    case obj?.isEventAttendByMentee &&
      obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentee &&
      !obj?.isEventAcceptByMentee:
      className = 'pr_event_msg';
      break;
    case !obj?.isEventAcceptByMentee &&
      obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentee &&
      obj?.isEventAttendByMentee:
      className = 'pr_event_msg_pair';
      break;
    case obj?.isEventAttendByMentee &&
      obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentee &&
      obj?.isEventAcceptByMentee:
      className = 'pr_event_msg_pair';
      break;
    case obj?.isEventAttendByMentee &&
      !obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentee &&
      obj?.isEventAcceptByMentee:
      className = 'pr_border_event_msg';
      break;
    case !obj?.isEventAttendByMentee &&
      obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentee &&
      !obj?.isEventAcceptByMentee:
      className = 'pr_border_onl_box';
      break;
    case !obj?.isEventAttendByMentee &&
      !obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentee &&
      !obj?.isEventAcceptByMentee:
      className = 'both_msg_box';
      break;
    case !obj?.isEventAttendByMentee &&
      !obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentee &&
      obj?.isEventAcceptByMentee:
      className = 'pr_event_invitation_accept';
      break;
    case obj?.isEventAttendByMentee &&
      !obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentee &&
      !obj?.isEventAcceptByMentee:
      className = 'pr_event_msg_ind';
      break;
    case obj?.isEventAttendByMentee &&
      !obj?.isSendPairPromptInWeek &&
      !obj?.isSendMessageInWeekByMentee &&
      obj?.isEventAcceptByMentee:
      className = 'pr_event_msg_ind';
      break;
    case !obj?.isEventAttendByMentee &&
      obj?.isSendPairPromptInWeek &&
      obj?.isSendMessageInWeekByMentee &&
      !obj?.isEventAcceptByMentee:
      className = 'pr_msg_box';
      break;
    case obj?.isEventAcceptByMentee &&
      !obj?.isEventAttendByMentee &&
      obj?.isSendMessageInWeekByMentee &&
      !obj?.isSendPairPromptInWeek:
      className = 'pr_border_not_event_msg';
      break;
    default:
      className = '';
  }
  return className;
}
export const replaceStartOfUrl = (url: any) => {
  let baseUrl;
  baseUrl = process.env.REACT_APP_CLOUDFRONT_URL;
  // Replace the starting part of the input URL with the baseUrl
  return url.replace(/^(https?:\/\/[^\/]+)/, baseUrl);
};
export const replaceShortNameAtIndex = (dataArray: any, newShortName: string, index: number) => {
  if (index === -1) {
    return dataArray;
  }
  const updatedArray = [...dataArray];
  const item = updatedArray[index];
  if (item.group && item.group.partner) {
    updatedArray[index] = {
      ...item,
      group: {
        ...item.group,
        groupName: newShortName,
        partner: {
          ...item.group.partner,
          shortName: newShortName,
        },
      },
    };
  }
  return updatedArray;
};

export function extractAllURLs(text: string): string[] {
  if (!text) return [];

  const urlPattern = /(https?:\/\/[^\s<>"'()]+)|(www\.[^\s<>"'()]+)|((?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<>"'()]*)?)/gi;

  const seen = new Set<string>();
  const results: string[] = [];
  let match: RegExpExecArray | null;

  while ((match = urlPattern.exec(text)) !== null) {
    const raw = match[0];
    const start = match.index;
    const end = start + raw.length;
    const isBareDomain = !!match[3];

    let token = raw.replace(/^[\s"'([<{]+|[\s"'.,:;!?)\]}>]+$/g, '').trim();
    if (!token) continue;

    if (isBareDomain && text.substr(end, 3) === '://') continue;

    if (isBareDomain && !token.includes('.')) continue;

    const candidate = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(token) ? token : `https://${token}`;

    try {
      const u = new URL(candidate);
      const normalized = u.href.replace(/\/$/, '');
      if (!seen.has(normalized)) {
        seen.add(normalized);
        results.push(normalized);
      }
    } catch {
      continue;
    }
  }

  return results;
}
export const getPromptType = (promptType: string | undefined) => {
  switch (promptType) {
    case PROMPT_TYPE_OPTIONS.MULTI_CHOICE.value:
      return PROMPT_TYPE_OPTIONS.MULTI_CHOICE.label;
    case PROMPT_TYPE_OPTIONS.OPEN_RESPONSE.value:
      return PROMPT_TYPE_OPTIONS.OPEN_RESPONSE.label;
    case PROMPT_TYPE_OPTIONS.NUDGE.value:
      return PROMPT_TYPE_OPTIONS.NUDGE.label;
    case PROMPT_TYPE_OPTIONS.PAIR_CONNECTION.value:
      return PROMPT_TYPE_OPTIONS.PAIR_CONNECTION.label;
    default:
      return null;
  }
};

export const useFieldBackspaceHandler = (
  promptMessage: string,
  setPromptData: (data: { promptMessage: string; insertedTemplateFieldNames?: string[] }) => void
) => {
  const getTemplateFieldRangeAtCursor = (text: string, cursorPos: number): { start: number; end: number } | null => {
    const regex = /{{[^{}]*}}/g;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      const start = match.index;
      const end = start + match[0].length;

      if (cursorPos >= start && cursorPos <= end) {
        return { start, end };
      }
    }

    return null;
  };

  return useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const textarea = e.currentTarget;

      const isArrowLeft = e.key === 'ArrowLeft';
      const isArrowRight = e.key === 'ArrowRight';
      if (
        (isArrowLeft || isArrowRight) &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        textarea.selectionStart === textarea.selectionEnd
      ) {
        const pos = textarea.selectionStart;
        const range = getTemplateFieldRangeAtCursor(promptMessage, pos);
        if (range && ((isArrowLeft && pos > range.start) || (isArrowRight && pos < range.end))) {
          e.preventDefault();
          const next = isArrowLeft ? range.start : range.end;
          textarea.setSelectionRange(next, next);
          return;
        }
      }

      if (e.key === 'Backspace') {
        const cursorPos = textarea.selectionStart;

        const beforeCursor = promptMessage.slice(0, cursorPos);
        const match = beforeCursor.match(/{{[^{}]*}}$/);

        if (match) {
          const start = cursorPos - match[0].length;
          const end = cursorPos;

          const updatedText = promptMessage.slice(0, start) + promptMessage.slice(end);

          const updatedFields = updatedText.match(/{{(.*?)}}/g)?.map(f => f.replace(/[{}]/g, '').trim()) || [];

          e.preventDefault();

          setPromptData({
            promptMessage: updatedText,
            insertedTemplateFieldNames: updatedFields,
          });

          setTimeout(() => {
            textarea.setSelectionRange(start, start);
          }, 0);
        }
      }
    },
    [promptMessage, setPromptData]
  );
};

export function highlightText(text: string): React.ReactNode {
  const regex = /{{(.*?)}}/g;
  const parts: React.ReactNode[] = [];

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const before = text.slice(lastIndex, match.index);
    const fieldName = match[1];

    if (before) parts.push(before);

    parts.push(
      <span key={match.index} className="template-wrapper">
        {/* cannot be replaced with `'{{'·+·fieldName·+·'}}'` */}
        <span className="template-filler">{'{{' + fieldName + '}}'}</span>
        <span className="template-label">{fieldName}</span>
      </span>
    );

    lastIndex = regex.lastIndex;
  }

  const remaining = text.slice(lastIndex);
  if (remaining) parts.push(remaining);

  return parts;
}

/**
 * Template fields that read the recipient's matched counterpart rather than the
 * recipient's own profile.
 */
export const RELATIONAL_TEMPLATE_FIELDS = ['Mentee First Name', 'Mentor First Name'];

const isRelationalTemplateField = (label: string): boolean => RELATIONAL_TEMPLATE_FIELDS.includes(label);

/**
 * IMP-2570 — the bulk Send Message field that reads the recipient's OWN first name
 * whatever their role, so one message can greet both sides of a pair. Unlike the
 * relational fields it never depends on a match.
 */
export const RECIPIENT_FIRST_NAME_FIELD = 'Recipient First Name';

/** The relational label that reads a recipient's OWN first name, given their role. */
const ownRoleRelationalLabel = (role: unknown): string | undefined => {
  if (role === 'Mentee') return 'Mentee First Name';
  if (role === 'Mentor') return 'Mentor First Name';
  return undefined;
};

const readOwnFirstName = (user: any): string | undefined => {
  const keys = ['preferredFirstName', 'preferredFname', 'firstName', 'fname'];
  return keys.map(key => user?.[key]).find(value => typeof value === 'string' && value.trim() !== '');
};

const hasCustomFieldValue = (user: any, label: string): boolean =>
  Boolean(
    user?.customFields?.some((field: { fieldName: string; value?: string; fieldValue?: string }) => {
      const value = field?.value ?? field?.fieldValue;
      return field?.fieldName === label && typeof value === 'string' && value.trim() !== '';
    })
  );

/**
 * Maps each paired user id to the first name of the person they are matched
 * with, from the pair list the recipient picker already loads.
 *
 * A user absent from this map has no match, which is what makes a relational
 * template field genuinely unresolvable for them rather than merely
 * inapplicable to their role.
 */
export function buildCounterpartFirstNameMap(pairs: any[] | undefined): Map<string, string> {
  const byUserId = new Map<string, string>();

  for (const pair of pairs ?? []) {
    const menteeId = pair?.mentee?.id;
    const mentorId = pair?.mentor?.id;
    const menteeName = readOwnFirstName(pair?.mentee);
    const mentorName = readOwnFirstName(pair?.mentor);

    // First pair wins, so the result does not depend on list order.
    if (menteeId && mentorName && !byUserId.has(String(menteeId))) {
      byUserId.set(String(menteeId), mentorName);
    }
    if (mentorId && menteeName && !byUserId.has(String(mentorId))) {
      byUserId.set(String(mentorId), menteeName);
    }
  }

  return byUserId;
}

/**
 * Whether a participants-table row is matched with a counterpart.
 *
 * Read from the row's own `status`, which is the same signal the participants
 * table shows as the "Matched" badge - so what the admin sees in the table and
 * whether the alternative message is required can never disagree.
 *
 * Deliberately NOT read from:
 *   - `assignedMentorId`: the participants list only projects it when the admin
 *     sorts by assigned mentor, so it is absent on an ordinary page load and
 *     every recipient would read as unmatched.
 *   - `suggestedMentor` / `matchesUsers`: those are potential matches, and
 *     treating a suggestion as a match would skip the alternative message for a
 *     recipient who has no counterpart at all.
 */
const MATCHED_USER_STATUSES = ['Matched', 'Matched Not Registered'];

export function isRecipientMatched(user: any): boolean {
  return MATCHED_USER_STATUSES.includes(user?.status);
}

export interface MissingTemplateFieldCounts {
  /**
   * Recipients missing at least one inserted field for any reason. Drives
   * whether the alternative message is offered at all - unchanged behavior for
   * ordinary custom fields the recipient happens to have no value for.
   */
  total: number;
  /**
   * Recipients whose relational field cannot resolve because they have no
   * matched counterpart. Drives whether the alternative message is *required*
   * and the send blocked. Recipients who do have a match are not
   * counted here, so a fully matched bulk send is never blocked.
   */
  unmatchedRelational: number;
}

/**
 * Counts recipients who would receive an unresolved template field.
 *
 * Shared by the composer's alternative-message control and the Send button's
 * enabled state, so what the admin is told and what the button allows can never
 * disagree.
 *
 * `hasCounterpart` answers, for an individually-selected recipient, whether
 * they are matched with anyone. It is a predicate rather than a name lookup
 * because that is all the count needs, and because the screens differ in what
 * they can see: the prompt composer has the pair list, while the participants
 * table only has each row's own match status. Pair-shaped recipients carry both
 * sides already and are read directly instead.
 *
 * A recipient's own-role name (`{{Mentee First Name}}` for a mentee) never
 * depends on a match, so it is read from their own profile.
 */
export function countRecipientsWithMissingTemplateFields(
  recipientList: any[] | undefined,
  insertedTemplateFieldNames: string[],
  hasCounterpart: (user: any) => boolean
): MissingTemplateFieldCounts {
  const fields = Array.isArray(insertedTemplateFieldNames) ? insertedTemplateFieldNames : [];

  if (!recipientList?.length || fields.length === 0) {
    return { total: 0, unmatchedRelational: 0 };
  }

  // Evaluates one individual user. `counterpartResolves` is whether the person
  // they are matched with can supply the name - false when they have no match.
  const evaluateUser = (user: any, counterpartResolves: boolean) => {
    let missingAny = false;
    let missingForLackOfMatch = false;

    for (const label of fields) {
      if (label === RECIPIENT_FIRST_NAME_FIELD) {
        if (!readOwnFirstName(user)) missingAny = true;
      } else if (isRelationalTemplateField(label)) {
        if (ownRoleRelationalLabel(user?.role) === label) {
          // The recipient's own name - resolvable whether or not they are matched.
          if (!readOwnFirstName(user)) missingAny = true;
        } else if (!counterpartResolves) {
          missingAny = true;
          missingForLackOfMatch = true;
        }
      } else if (!hasCustomFieldValue(user, label)) {
        missingAny = true;
      }
    }

    return { missingAny, missingForLackOfMatch };
  };

  let total = 0;
  let unmatchedRelational = 0;

  const tally = (user: any) => {
    const { missingAny, missingForLackOfMatch } = evaluateUser(user, hasCounterpart(user));
    if (missingAny) total += 1;
    if (missingForLackOfMatch) unmatchedRelational += 1;
  };

  for (const recipient of recipientList) {
    // A pair-shaped recipient carries both sides, so each side is the other's
    // counterpart and neither can ever be unmatched. The counterpart's name is
    // right there, so it is read directly rather than through the predicate.
    //
    // Counted once per pair rather than once per side, preserving the existing
    // behavior established when double-counting was fixed - a pair chip and the
    // merged pair it came from must not add up differently.
    if (recipient?.mentee && recipient?.mentor) {
      const mentee = evaluateUser({ ...recipient.mentee, role: 'Mentee' }, Boolean(readOwnFirstName(recipient.mentor)));
      const mentor = evaluateUser({ ...recipient.mentor, role: 'Mentor' }, Boolean(readOwnFirstName(recipient.mentee)));

      if (mentee.missingAny || mentor.missingAny) total += 1;
      continue;
    }

    if (recipient?.role === 'Group' && Array.isArray(recipient.members)) {
      for (const member of recipient.members) {
        tally(member);
      }
      continue;
    }

    tally(recipient);
  }

  return { total, unmatchedRelational };
}

export const getAttachedAudioDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    const url = URL.createObjectURL(file);

    audio.preload = 'metadata';
    audio.src = url;

    audio.onloadedmetadata = () => {
      if (!isNaN(audio.duration)) {
        URL.revokeObjectURL(url);
        resolve(audio.duration);
      }
    };

    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load audio metadata'));
    };
  });
};
